import {isOk} from "../../utils/apiResponse";

/**
 * Lo que el formulario de dashbo.io ya le preguntó a la agencia, leído de `agency-lead/prefill/`
 * (contrato de la tanda 2026-09-30-alta-leo, §1 — card VELQYFFQ).
 *
 * Devuelve `null` cuando no hay nada que prellenar: el token no existe, venció o ya dio de alta
 * otra agencia (404), el usuario ya tiene agencia (403), la red falló, o la respuesta no trae
 * ninguno de los dos datos obligatorios. En todos esos casos el alta sigue como siempre, con el
 * formulario vacío: el prefill ahorra tipeo, nunca bloquea.
 *
 * La forma la confirmó el Backend (frente leo-be, 30/09): los campos van en la RAÍZ junto con
 * `status: 'OK'`, y los vacíos llegan como `''`. Se tolera además el envoltorio de `OKResponse`
 * (`{status: 'OK', data: {...}}`), que es la forma habitual de las vistas: si un día cambia, el
 * prefill no se apaga en silencio.
 */
export function readLeadPrefill(response) {
    if (!response || typeof response !== 'object') return null;
    const body = isOk(response) && response.data && typeof response.data === 'object'
        ? response.data
        : response;
    if (typeof body.agency_name !== 'string' && typeof body.contact_name !== 'string') return null;

    const agencyName = clean(body.agency_name);
    const contactName = clean(body.contact_name);
    return {
        agencyName,
        contactName,
        phone: clean(body.phone),
        website: clean(body.website),
        // El backend decide `complete`, pero el salto del formulario además exige que los dos
        // obligatorios lleguen con texto: si el flag y los datos no coinciden, se postearía un alta
        // vacía que el backend rebota, y el usuario vería un error que no causó.
        complete: body.complete === true && agencyName !== '' && contactName !== '',
    };
}

function clean(value) {
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * El token del lead vive una semana en `localStorage` y sobrevive al logout, así que en un navegador
 * compartido puede ser de OTRA persona: A completa dashbo.io y no termina el alta, B entra después
 * con su cuenta sin agencia, y el prefill le crearía a B una agencia con el nombre, el teléfono y el
 * sitio de A, sin mostrarle nada (review C-registro-03).
 *
 * Por eso el prefill —y con él el salto sin formulario— sólo corre con un token que llegó de
 * dashbo.io EN ESTA PESTAÑA: `/signup` lo marca en `sessionStorage`, que no se comparte entre
 * pestañas y muere al cerrarlas. El login con Google (`GoogleLogin` de `@react-oauth/google`) no
 * saca a la pestaña del sitio, y aunque navegara, `sessionStorage` sobrevive dentro de la misma
 * pestaña: la marca sigue ahí cuando llega «Crea tu agencia». Un token sin marca se sigue mandando
 * en el alta como atribución, como siempre; lo único que no hace es prellenar.
 */
const FRESH_LEAD_KEY = 'signup_lead_fresh_token';

export function markLeadTokenFresh(token) {
    if (!token) return;
    try {
        sessionStorage.setItem(FRESH_LEAD_KEY, token);
    } catch (e) {
        // Sin storage no hay prefill: el alta sigue con el formulario vacío, como antes.
    }
}

export function isLeadTokenFresh(token) {
    try {
        return Boolean(token) && sessionStorage.getItem(FRESH_LEAD_KEY) === token;
    } catch (e) {
        return false;
    }
}

export function clearLeadTokenFresh() {
    try {
        sessionStorage.removeItem(FRESH_LEAD_KEY);
    } catch (e) {
        // Nada que limpiar.
    }
}

/**
 * Los errores POR CAMPO de una respuesta del alta, o `{}` si no hay.
 *
 * `ErrorResponse` del backend manda SIEMPRE `errors: json.dumps(extra_data or {})`, así que un código
 * tipado (`ERROR_VALIDATION`, `EMAIL_IS_AGENCY_USER`…) llega con `errors: "{}"`, que es truthy; y un
 * 401/403/429 de DRF llega con el `errors: []` que completa el `APIClient`, y `JSON.parse([])` lanza.
 * Tratar cualquiera de los dos como «errores por campo» dejaba la pantalla muda, o colgada con el
 * spinner (review C-registro-01).
 */
export function fieldErrorsOf(response) {
    if (!response || typeof response.errors !== 'string') return {};
    try {
        const parsed = JSON.parse(response.errors);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
        return {};
    }
}
